import { useState, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Calendar } from "../components/ui/calendar";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { 
  Clock, 
  LogIn, 
  LogOut, 
  CalendarDays,
  Building2,
  Upload,
  Download,
  Fingerprint,
  FileSpreadsheet,
  Pencil,
  Trash2,
  MoreHorizontal,
  CheckCircle,
} from "lucide-react";
import { format } from "date-fns";
import { useAttendance } from "../hooks";
// Extracted components
import { AttendanceStats } from "../components/attendance";

const Attendance = () => {
  const { currentBusiness } = useAuth();
  
  // Use custom hook for data fetching
  const {
    attendance,
    employees,
    loading,
    stats,
    selectedDate,
    setSelectedDate,
    checkIn,
    checkOut,
    updateAttendance,
    deleteAttendance,
    importBiometric,
    downloadTemplate,
    exportAttendance,
    exportAttendanceSalary,
    getEmployeeName,
  } = useAttendance(currentBusiness?.id);

  // Local UI state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [checkoutRecordId, setCheckoutRecordId] = useState(null);
  const [checkoutTime, setCheckoutTime] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [checkInTime, setCheckInTime] = useState("");
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editFormData, setEditFormData] = useState({
    check_in: "",
    check_out: "",
    status: "present",
    notes: ""
  });
  
  // Export form state
  const [exportStartDate, setExportStartDate] = useState(null);
  const [exportEndDate, setExportEndDate] = useState(null);
  const [exportEmployee, setExportEmployee] = useState("all");
  const [exportFormat, setExportFormat] = useState("excel");
  const [exportReportType, setExportReportType] = useState("attendance"); // "attendance" or "attendance-salary"
  const [exportMonth, setExportMonth] = useState(new Date().getMonth() + 1);
  const [exportYear, setExportYear] = useState(new Date().getFullYear());

  const handleCheckIn = async () => {
    if (!selectedEmployee) {
      toast.error("Please select an employee");
      return;
    }
    try {
      const time = checkInTime || null;
      await checkIn(selectedEmployee, time);
      toast.success("Check-in recorded");
      setDialogOpen(false);
      setSelectedEmployee("");
      setCheckInTime("");
    } catch (error) {
      toast.error("Failed to record check-in");
    }
  };

  const openCheckoutDialog = (attendanceId) => {
    setCheckoutRecordId(attendanceId);
    setCheckoutTime("");
    setCheckoutDialogOpen(true);
  };

  const handleCheckOut = async () => {
    try {
      const time = checkoutTime || null;
      await checkOut(checkoutRecordId, time);
      toast.success("Check-out recorded");
      setCheckoutDialogOpen(false);
      setCheckoutRecordId(null);
      setCheckoutTime("");
    } catch (error) {
      toast.error("Failed to record check-out");
    }
  };

  const openEditDialog = (record) => {
    setEditingRecord(record);
    setEditFormData({
      check_in: record.check_in || "",
      check_out: record.check_out || "",
      status: record.status || "present",
      notes: record.notes || ""
    });
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!editingRecord) return;
    try {
      await updateAttendance(editingRecord.id, editFormData);
      toast.success("Attendance record updated");
      setEditDialogOpen(false);
      setEditingRecord(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update attendance");
    }
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm("Are you sure you want to delete this attendance record?")) return;
    try {
      await deleteAttendance(recordId);
      toast.success("Attendance record deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete attendance");
    }
  };

  const handleExportCSV = async () => {
    // For combined Attendance & Salary report
    if (exportReportType === "attendance-salary") {
      setExporting(true);
      try {
        const blob = await exportAttendanceSalary(exportMonth, exportYear);
        const downloadUrl = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = downloadUrl;
        
        const monthNames = ["January", "February", "March", "April", "May", "June", 
                           "July", "August", "September", "October", "November", "December"];
        link.setAttribute('download', `Attendance_Salary_${monthNames[exportMonth - 1]}_${exportYear}.xlsx`);
        
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success("Attendance & Salary report downloaded");
        setExportDialogOpen(false);
      } catch (error) {
        toast.error("Failed to export report");
      } finally {
        setExporting(false);
      }
      return;
    }

    // Standard date-range attendance export
    if (!exportStartDate || !exportEndDate) {
      toast.error("Please select both start and end dates");
      return;
    }
    
    setExporting(true);
    try {
      const blob = await exportAttendance(exportStartDate, exportEndDate, exportEmployee, exportFormat);
      const downloadUrl = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      const startStr = format(exportStartDate, "yyyy-MM-dd");
      const endStr = format(exportEndDate, "yyyy-MM-dd");
      const empName = exportEmployee !== "all" ? employees.find(e => e.id === exportEmployee) : null;
      const empPart = empName ? `_${empName.first_name}_${empName.last_name}` : "";
      const ext = exportFormat === 'excel' ? 'xlsx' : exportFormat;
      link.setAttribute('download', `attendance${empPart}_${startStr}_to_${endStr}.${ext}`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export completed");
      setExportDialogOpen(false);
    } catch (error) {
      toast.error("Failed to export attendance");
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'attendance_biometric_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const handleImportBiometric = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportResult(null);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const result = await importBiometric(formData);
      setImportResult(result);
      
      if (result.imported > 0 || result.updated > 0) {
        toast.success(`Imported: ${result.imported} new, ${result.updated} updated`);
      }
      if (result.skipped > 0 && result.imported === 0 && result.updated === 0) {
        toast.error(`All ${result.skipped} records skipped. Check errors below.`);
      } else if (result.skipped > 0) {
        toast.warning(`${result.skipped} records skipped`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import attendance");
      setImportResult({ imported: 0, updated: 0, skipped: 0, errors: [error.response?.data?.detail || "Upload failed"] });
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (!currentBusiness) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <Building2 className="h-16 w-16 text-slate-300 mb-4" />
        <h2 className="text-2xl font-semibold text-slate-800 font-heading mb-2">No Business Selected</h2>
        <p className="text-slate-500">Select a business to manage attendance</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="attendance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">Attendance</h1>
          <p className="text-slate-500">Track daily attendance</p>
        </div>
        <div className="flex flex-wrap gap-3">
          {/* Export Dialog */}
          <Dialog open={exportDialogOpen} onOpenChange={(open) => { 
            setExportDialogOpen(open); 
            if (!open) {
              setExportStartDate(null);
              setExportEndDate(null);
              setExportEmployee("all");
              setExportReportType("attendance");
            }
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-full" data-testid="export-attendance-btn">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  <Download className="h-5 w-5 text-primary" />
                  Export Reports
                </DialogTitle>
                <DialogDescription>
                  Generate attendance and salary reports
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {/* Report Type Selection */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Report Type</label>
                  <Select value={exportReportType} onValueChange={setExportReportType}>
                    <SelectTrigger className="input-field" data-testid="export-report-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="attendance">Attendance (Date Range)</SelectItem>
                      <SelectItem value="attendance-salary">Attendance & Salary (Monthly)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {exportReportType === "attendance-salary" ? (
                  /* Attendance & Salary Monthly Export */
                  <>
                    <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg text-sm">
                      <p className="text-violet-700 font-medium">Combined Report</p>
                      <p className="text-violet-600 text-xs mt-1">
                        Generates two sheets: Attendance grid (day-by-day with P/WO/L/A markers) and Salary breakdown (BASIC, DA, HRA, deductions, NET).
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">Month</label>
                        <Select value={String(exportMonth)} onValueChange={(v) => setExportMonth(Number(v))}>
                          <SelectTrigger className="input-field" data-testid="export-month-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {["January", "February", "March", "April", "May", "June", 
                              "July", "August", "September", "October", "November", "December"].map((m, i) => (
                              <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">Year</label>
                        <Select value={String(exportYear)} onValueChange={(v) => setExportYear(Number(v))}>
                          <SelectTrigger className="input-field" data-testid="export-year-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[2024, 2025, 2026, 2027].map(y => (
                              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" className="text-xs" onClick={() => {
                        const now = new Date();
                        setExportMonth(now.getMonth() + 1);
                        setExportYear(now.getFullYear());
                      }}>This Month</Button>
                      <Button variant="outline" size="sm" className="text-xs" onClick={() => {
                        const now = new Date();
                        const lastMonth = now.getMonth() === 0 ? 12 : now.getMonth();
                        const lastYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
                        setExportMonth(lastMonth);
                        setExportYear(lastYear);
                      }}>Last Month</Button>
                    </div>
                  </>
                ) : (
                  /* Standard Date Range Export */
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">Employee</label>
                      <Select value={exportEmployee} onValueChange={setExportEmployee}>
                        <SelectTrigger className="input-field" data-testid="export-employee-select">
                          <SelectValue placeholder="Select employee" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Employees</SelectItem>
                          {employees.map(emp => (
                            <SelectItem key={emp.id} value={emp.id}>
                              {emp.first_name} {emp.last_name} {emp.employee_code ? `(${emp.employee_code})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">Start Date</label>
                        <Input
                          type="date"
                          value={exportStartDate ? format(exportStartDate, "yyyy-MM-dd") : ""}
                          onChange={(e) => setExportStartDate(e.target.value ? new Date(e.target.value) : null)}
                          className="input-field"
                          data-testid="export-start-date-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">End Date</label>
                        <Input
                          type="date"
                          value={exportEndDate ? format(exportEndDate, "yyyy-MM-dd") : ""}
                          onChange={(e) => setExportEndDate(e.target.value ? new Date(e.target.value) : null)}
                          className="input-field"
                          data-testid="export-end-date-input"
                        />
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" className="text-xs" onClick={() => {
                        const today = new Date();
                        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                        setExportStartDate(firstDay);
                        setExportEndDate(today);
                      }}>This Month</Button>
                      <Button variant="outline" size="sm" className="text-xs" onClick={() => {
                        const today = new Date();
                        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                        const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
                        setExportStartDate(lastMonth);
                        setExportEndDate(lastDayLastMonth);
                      }}>Last Month</Button>
                      <Button variant="outline" size="sm" className="text-xs" onClick={() => {
                        const today = new Date();
                        const sevenDaysAgo = new Date(today);
                        sevenDaysAgo.setDate(today.getDate() - 7);
                        setExportStartDate(sevenDaysAgo);
                        setExportEndDate(today);
                      }}>Last 7 Days</Button>
                    </div>

                    {exportStartDate && exportEndDate && (
                      <div className="p-3 bg-slate-50 rounded-lg text-sm">
                        <p className="text-slate-600">
                          <span className="font-medium">Report: </span>
                          {exportEmployee === "all" ? "All Employees" : employees.find(e => e.id === exportEmployee)?.first_name + " " + employees.find(e => e.id === exportEmployee)?.last_name}
                        </p>
                        <p className="text-slate-600">
                          <span className="font-medium">Period: </span>
                          {format(exportStartDate, "MMM dd, yyyy")} - {format(exportEndDate, "MMM dd, yyyy")}
                        </p>
                      </div>
                    )}

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">Export Format</label>
                      <Select value={exportFormat} onValueChange={setExportFormat}>
                        <SelectTrigger className="input-field" data-testid="export-format-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="excel">Excel (.xlsx)</SelectItem>
                          <SelectItem value="csv">CSV</SelectItem>
                          <SelectItem value="pdf">PDF</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}

                <Button
                  className="w-full btn-primary"
                  onClick={handleExportCSV}
                  disabled={exportReportType === "attendance" && (!exportStartDate || !exportEndDate) || exporting}
                  data-testid="export-submit-btn"
                >
                  {exporting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      {exportReportType === "attendance-salary" ? "Download Attendance & Salary Report" : "Download Report"}
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Biometric Import Dialog */}
          <Dialog open={importDialogOpen} onOpenChange={(open) => { setImportDialogOpen(open); if (!open) setImportResult(null); }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-full" data-testid="import-biometric-btn">
                <Fingerprint className="h-4 w-4 mr-2" />
                Import Biometric
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  <Fingerprint className="h-5 w-5 text-primary" />
                  Import Biometric Data
                </DialogTitle>
                <DialogDescription>
                  Upload attendance data from your biometric device or CSV file
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <Button variant="outline" className="w-full gap-2" onClick={handleDownloadTemplate} data-testid="download-attendance-template-btn">
                  <FileSpreadsheet className="h-4 w-4" />
                  Download CSV Template
                </Button>

                <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl space-y-3">
                  <h4 className="font-medium text-slate-800">CSV Format:</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="bg-white/60">
                          <th className="border border-slate-200 px-2 py-1 text-left font-semibold">employee_name</th>
                          <th className="border border-slate-200 px-2 py-1 text-left font-semibold">employee_email</th>
                          <th className="border border-slate-200 px-2 py-1 text-left font-semibold">date</th>
                          <th className="border border-slate-200 px-2 py-1 text-left font-semibold">check_in</th>
                          <th className="border border-slate-200 px-2 py-1 text-left font-semibold">check_out</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-slate-200 px-2 py-1">John Doe</td>
                          <td className="border border-slate-200 px-2 py-1">john@co.com</td>
                          <td className="border border-slate-200 px-2 py-1">2026-02-27</td>
                          <td className="border border-slate-200 px-2 py-1">09:00</td>
                          <td className="border border-slate-200 px-2 py-1">18:00</td>
                        </tr>
                        <tr className="bg-white/40">
                          <td className="border border-slate-200 px-2 py-1">Jane Smith</td>
                          <td className="border border-slate-200 px-2 py-1"></td>
                          <td className="border border-slate-200 px-2 py-1">2026-02-27</td>
                          <td className="border border-slate-200 px-2 py-1">09:15</td>
                          <td className="border border-slate-200 px-2 py-1">17:45</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <ul className="text-xs text-slate-600 space-y-1">
                    <li><span className="font-semibold">Match by:</span> employee_name, employee_email, or employee_code</li>
                    <li><span className="font-semibold">Date format:</span> YYYY-MM-DD (e.g. 2026-02-27)</li>
                    <li><span className="font-semibold">Time format:</span> HH:MM (24-hour, e.g. 09:00, 18:30)</li>
                    <li><span className="font-semibold">Optional columns:</span> status, notes</li>
                    <li>Also supports Excel (.xlsx) from biometric devices</li>
                    <li>Existing records are updated, not duplicated</li>
                  </ul>
                </div>

                <div className="border-2 border-dashed border-purple-200 rounded-xl p-6 text-center bg-purple-50/30">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleImportBiometric}
                    className="hidden"
                    id="biometric-upload"
                    data-testid="biometric-upload-input"
                  />
                  <label htmlFor="biometric-upload" className="cursor-pointer">
                    <Upload className="h-10 w-10 mx-auto text-purple-400 mb-2" />
                    <p className="text-slate-600 font-medium">Click to upload</p>
                    <p className="text-sm text-slate-400">.csv or .xlsx files</p>
                  </label>
                </div>
                {importing && (
                  <div className="flex items-center justify-center gap-2 text-primary">
                    <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full"></div>
                    Processing...
                  </div>
                )}
                {importResult && (
                  <div className="space-y-3" data-testid="biometric-import-result">
                    <div className={`p-3 rounded-lg ${(importResult.imported > 0 || importResult.updated > 0) ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                      <p className="font-medium text-sm">
                        {importResult.imported > 0 && `${importResult.imported} new records imported`}
                        {importResult.imported > 0 && importResult.updated > 0 && ', '}
                        {importResult.updated > 0 && `${importResult.updated} updated`}
                        {importResult.imported === 0 && importResult.updated === 0 && 'No records imported'}
                        {importResult.skipped > 0 && ` (${importResult.skipped} skipped)`}
                      </p>
                    </div>
                    {importResult.employees_created > 0 && (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-xs font-medium text-blue-800 mb-1">
                          {importResult.employees_created} employees auto-created:
                        </p>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {importResult.created_employees?.map((emp, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs text-blue-700">
                              <span className="font-mono font-semibold bg-blue-100 px-1.5 py-0.5 rounded">{emp.code}</span>
                              <span>{emp.name}</span>
                              {emp.department && <span className="text-blue-500">({emp.department})</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {importResult.errors?.length > 0 && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg max-h-32 overflow-y-auto">
                        <p className="text-xs font-medium text-amber-800 mb-1">Unmatched employees ({importResult.errors.length}):</p>
                        <ul className="text-xs text-amber-700 space-y-0.5">
                          {importResult.errors.slice(0, 20).map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="mark-attendance-btn">
                <Clock className="h-4 w-4 mr-2" />
                Mark Attendance
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading">Mark Attendance</DialogTitle>
                <DialogDescription>Record check-in for an employee</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Select Employee</label>
                  <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                    <SelectTrigger className="input-field" data-testid="attendance-employee-select">
                      <SelectValue placeholder="Choose employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.employee_code && <span className="text-xs font-mono text-primary mr-1">{emp.employee_code}</span>}
                          {emp.first_name} {emp.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Check-In Time</label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                    <input
                      type="time"
                      value={checkInTime}
                      onChange={(e) => setCheckInTime(e.target.value)}
                      placeholder={format(new Date(), "HH:mm")}
                      className="flex h-10 w-full rounded-xl border border-transparent bg-slate-50 px-3 py-2 text-sm focus:bg-white focus:border-primary/30 focus:ring-4 focus:ring-primary/10 transition-all duration-200"
                      style={{ paddingLeft: '2.5rem' }}
                      data-testid="checkin-time-input"
                    />
                  </div>
                  <p className="text-xs text-slate-400">Leave empty to use current time ({format(new Date(), "HH:mm")})</p>
                </div>
                <Button onClick={handleCheckIn} className="w-full btn-primary" data-testid="check-in-btn">
                  <LogIn className="h-4 w-4 mr-2" />
                  Check In
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats - using AttendanceStats component */}
      <AttendanceStats 
        stats={{
          totalEmployees: stats.total,
          presentToday: stats.present,
          absentToday: stats.absent,
          lateToday: stats.late || 0,
        }} 
      />

      {/* Calendar & Table */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="card-soft lg:col-span-1" data-testid="attendance-calendar">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Select Date</CardTitle>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              className="rounded-md"
            />
          </CardContent>
        </Card>

        <Card className="card-soft lg:col-span-2" data-testid="attendance-table">
          <CardHeader>
            <CardTitle className="font-heading text-lg">
              Attendance for {format(selectedDate, "MMMM d, yyyy")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
              </div>
            ) : attendance.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                No attendance records for this date
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="text-xs uppercase tracking-wide text-slate-400">Employee</TableHead>
                      <TableHead className="text-xs uppercase tracking-wide text-slate-400">Check In</TableHead>
                      <TableHead className="text-xs uppercase tracking-wide text-slate-400">Check Out</TableHead>
                      <TableHead className="text-xs uppercase tracking-wide text-slate-400">Status</TableHead>
                      <TableHead className="text-xs uppercase tracking-wide text-slate-400">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {attendance.map((record) => (
                      <TableRow key={record.id} className="hover:bg-slate-50/50" data-testid={`attendance-row-${record.id}`}>
                        <TableCell className="font-medium text-slate-800">
                          {getEmployeeName(record.employee_id)}
                        </TableCell>
                        <TableCell>
                          <span className="flex items-center gap-2 text-slate-600">
                            <LogIn className="h-4 w-4 text-green-600" />
                            {record.check_in || "-"}
                          </span>
                        </TableCell>
                        <TableCell>
                          {record.check_out ? (
                            <span className="flex items-center gap-2 text-slate-600">
                              <LogOut className="h-4 w-4 text-red-600" />
                              {record.check_out}
                            </span>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openCheckoutDialog(record.id)}
                              className="rounded-full text-xs h-7 px-3 border-orange-200 text-orange-700 hover:bg-orange-50"
                              data-testid={`checkout-btn-${record.id}`}
                            >
                              <LogOut className="h-3 w-3 mr-1" />
                              Check Out
                            </Button>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline" 
                            className={
                              record.status === "present" 
                                ? "bg-green-50 text-green-700 border-green-200" 
                                : record.status === "half_day"
                                ? "bg-yellow-50 text-yellow-700 border-yellow-200"
                                : record.status === "half_day_absent"
                                ? "bg-orange-50 text-orange-700 border-orange-200"
                                : record.status === "on_leave"
                                ? "bg-blue-50 text-blue-700 border-blue-200"
                                : record.status === "weekly_off"
                                ? "bg-purple-50 text-purple-700 border-purple-200"
                                : record.status === "late"
                                ? "bg-amber-50 text-amber-700 border-amber-200"
                                : "bg-red-50 text-red-700 border-red-200"
                            }
                          >
                            {record.status === "weekly_off" ? "Weekly Off" 
                              : record.status === "half_day" ? "Half Day" 
                              : record.status === "half_day_absent" ? "Half Day Absent"
                              : record.status === "on_leave" ? "On Leave" 
                              : record.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`attendance-actions-${record.id}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditDialog(record)} data-testid={`edit-attendance-${record.id}`}>
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleDelete(record.id)} 
                                className="text-red-600"
                                data-testid={`delete-attendance-${record.id}`}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Checkout Time Dialog */}
      <Dialog open={checkoutDialogOpen} onOpenChange={setCheckoutDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Record Check-Out</DialogTitle>
            <DialogDescription>Set the check-out time for this employee</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Check-Out Time</label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                <input
                  type="time"
                  value={checkoutTime}
                  onChange={(e) => setCheckoutTime(e.target.value)}
                  className="flex h-10 w-full rounded-xl border border-transparent bg-slate-50 px-3 py-2 text-sm focus:bg-white focus:border-primary/30 focus:ring-4 focus:ring-primary/10 transition-all duration-200"
                  style={{ paddingLeft: '2.5rem' }}
                  data-testid="checkout-time-input"
                />
              </div>
              <p className="text-xs text-slate-400">Leave empty to use current time ({format(new Date(), "HH:mm")})</p>
            </div>
            <Button onClick={handleCheckOut} className="w-full btn-primary" data-testid="confirm-checkout-btn">
              <LogOut className="h-4 w-4 mr-2" />
              Confirm Check-Out
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Attendance Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Attendance</DialogTitle>
            <DialogDescription>
              {editingRecord && `Update attendance for ${getEmployeeName(editingRecord.employee_id)} - ${editingRecord.date}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Check-In Time</Label>
                <div className="relative">
                  <LogIn className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-600 pointer-events-none" />
                  <Input
                    type="time"
                    value={editFormData.check_in}
                    onChange={(e) => setEditFormData({...editFormData, check_in: e.target.value})}
                    className="pl-10"
                    data-testid="edit-checkin-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Check-Out Time</Label>
                <div className="relative">
                  <LogOut className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-red-600 pointer-events-none" />
                  <Input
                    type="time"
                    value={editFormData.check_out}
                    onChange={(e) => setEditFormData({...editFormData, check_out: e.target.value})}
                    className="pl-10"
                    data-testid="edit-checkout-input"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Status</Label>
              <Select 
                value={editFormData.status} 
                onValueChange={(value) => setEditFormData({...editFormData, status: value})}
              >
                <SelectTrigger className="input-field" data-testid="edit-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="present">Present</SelectItem>
                  <SelectItem value="absent">Absent</SelectItem>
                  <SelectItem value="half_day">Half Day (Leave)</SelectItem>
                  <SelectItem value="half_day_absent">Half Day Absent (LOP)</SelectItem>
                  <SelectItem value="on_leave">On Leave</SelectItem>
                  <SelectItem value="weekly_off">Weekly Off</SelectItem>
                  <SelectItem value="late">Late</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Notes</Label>
              <Input
                value={editFormData.notes}
                onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                placeholder="Optional notes..."
                data-testid="edit-notes-input"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button 
                variant="outline" 
                onClick={() => setEditDialogOpen(false)} 
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleEditSave} 
                className="flex-1 btn-primary"
                data-testid="save-edit-attendance-btn"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Attendance;
